// Phase 14: Custom 404 Page with City Suggestions & Nearby Areas
import { Link, useLocation } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Home, Phone, Search, ArrowRight, FileText, Wrench, MapPin } from "lucide-react";
import PublicLayout from "@/components/layout/PublicLayout";
import { COMPANY } from "@/lib/constants";
import { CITY_LINK_POOL } from "@/lib/topicClusters";

// Parse URL to suggest nearby cities
const extractCityFromUrl = (pathname: string): string | null => {
  // Common patterns: /roofing-naples, /naples-roofing, /roofing/naples, /naples
  const patterns = [
    /\/roofing-([a-z-]+)/i,
    /\/([a-z-]+)-roofing/i,
    /\/roofing\/([a-z-]+)/i,
    /\/([a-z-]+)$/i,
  ];
  
  for (const pattern of patterns) {
    const match = pathname.match(pattern);
    if (match && match[1]) {
      return match[1].toLowerCase();
    }
  }
  return null;
};

// Find similar cities based on slug
const findSimilarCities = (attemptedSlug: string | null) => {
  if (!attemptedSlug) return CITY_LINK_POOL.slice(0, 6);
  
  // Try exact match first
  const exactMatch = CITY_LINK_POOL.find(c => c.slug === attemptedSlug);
  if (exactMatch) {
    // Return same-county cities
    const sameCounty = CITY_LINK_POOL.filter(c => c.county === exactMatch.county && c.slug !== attemptedSlug);
    return sameCounty.length > 0 ? sameCounty.slice(0, 6) : CITY_LINK_POOL.slice(0, 6);
  }
  
  // Partial match - find cities that contain parts of the slug
  const partialMatches = CITY_LINK_POOL.filter(c => 
    c.slug.includes(attemptedSlug) || attemptedSlug.includes(c.slug) ||
    c.name.toLowerCase().includes(attemptedSlug.replace(/-/g, " "))
  );
  
  return partialMatches.length > 0 ? partialMatches.slice(0, 6) : CITY_LINK_POOL.slice(0, 6);
};

const NotFound = () => {
  const location = useLocation();
  
  const attemptedCity = useMemo(() => extractCityFromUrl(location.pathname), [location.pathname]);
  const suggestedCities = useMemo(() => findSimilarCities(attemptedCity), [attemptedCity]);

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname]);

  const breadcrumbs = [
    { name: "Home", url: "https://obriencontractingusa.com/" },
    { name: "Page Not Found", url: `https://obriencontractingusa.com${location.pathname}` }
  ];

  const popularPages = [
    { name: "Get a Free Estimate", href: "/quick-estimate", icon: FileText },
    { name: "Our Services", href: "/services", icon: Wrench },
    { name: "Contact Us", href: "/contact", icon: Phone },
    { name: "AI Roof Visualizer", href: "/roof-visualizer", icon: Search },
  ];

  return (
    <PublicLayout
      title="Page Not Found"
      description={`The page you're looking for doesn't exist. Browse ${COMPANY.name}'s roofing services, get a free estimate, or contact us for help.`}
      breadcrumbs={breadcrumbs}
    >
      <Helmet>
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <section className="py-16 md:py-24 bg-secondary min-h-[60vh]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            {/* 404 Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-destructive/10 mb-6">
              <span className="text-4xl font-heading font-bold text-destructive">404</span>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Page Not Found
            </h1>
            
            <p className="text-muted-foreground text-lg mb-8">
              Sorry, we couldn't find the page you're looking for. It may have been moved, 
              deleted, or you may have mistyped the URL.
            </p>

            {/* Primary Action */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link to="/">
                <Button variant="accent" size="lg">
                  <Home className="mr-2 h-4 w-4" />
                  Back to Home
                </Button>
              </Link>
              <a href={COMPANY.phoneHref}>
                <Button variant="outline" size="lg">
                  <Phone className="mr-2 h-4 w-4" />
                  Call {COMPANY.phoneFormatted}
                </Button>
              </a>
            </div>

            {/* City Suggestions - If URL looks like a city page */}
            {attemptedCity && (
              <div className="bg-card rounded-xl p-6 shadow-soft mb-6">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <MapPin className="h-5 w-5 text-accent" />
                  <h2 className="text-lg font-heading font-bold text-foreground">
                    Looking for a Service Area?
                  </h2>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  We serve these nearby locations:
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {suggestedCities.map((city) => (
                    <Link
                      key={city.slug}
                      to={`/roofing/${city.slug}`}
                      className="flex items-center gap-2 p-3 rounded-lg bg-secondary hover:bg-accent/10 transition-colors"
                    >
                      <span className="text-foreground text-sm">{city.name}</span>
                      <ArrowRight className="h-3 w-3 text-muted-foreground ml-auto" />
                    </Link>
                  ))}
                </div>
                <Link 
                  to="/roofing" 
                  className="inline-flex items-center gap-1 mt-4 text-accent hover:underline text-sm"
                >
                  View all service areas <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            )}

            {/* Popular Pages */}
            <div className="bg-card rounded-xl p-6 shadow-soft">
              <h2 className="text-lg font-heading font-bold text-foreground mb-4">
                Popular Pages
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {popularPages.map((page) => (
                  <Link
                    key={page.href}
                    to={page.href}
                    className="flex items-center gap-3 p-3 rounded-lg bg-secondary hover:bg-accent/10 transition-colors group"
                  >
                    <page.icon className="h-5 w-5 text-accent" />
                    <span className="text-foreground group-hover:text-accent transition-colors">
                      {page.name}
                    </span>
                    <ArrowRight className="h-4 w-4 text-muted-foreground ml-auto group-hover:text-accent transition-colors" />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default NotFound;
